import { Link, useLocation, useNavigate } from '@tanstack/react-router'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@trellis/roots/dropdown-menu'
import { useIsMobile } from '@trellis/roots/lib/useIsMobile'
import { Avatar, AvatarLabel } from '@trellis/roots/media'
import { AppSidebar as RootsAppSidebar } from '@trellis/roots/navigation'
import type { NavSection } from '@trellis/roots/navigation'
import { Tooltip, TooltipContent, TooltipTrigger } from '@trellis/roots/tooltip'
import { Typography } from '@trellis/roots/typography'
import { DOCS_URL, SUPPORT_EMAIL } from '@trellis/shared'
import {
  BarChart2,
  BookOpen,
  Check,
  ChevronsUpDown,
  ClipboardCheck,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  LogOut,
  Package,
  Receipt,
  ScrollText,
  Settings,
  Users,
} from 'lucide-react'
import { useTheme } from 'next-themes'

import { logoMarkDark, logoMarkLight } from '@/assets'
import { TWO_FACTOR_BANNER_DISMISSED_KEY } from '@/components/two-factor'
import { env } from '@/env'
import { useOrganization } from '@/hooks/useOrganization'
import { useTenantMemberships } from '@/hooks/useTenantMemberships'
import { authClient } from '@/lib/auth-client'
import { resolveSmallLogoUrl } from '@/lib/organization-branding.utils'
import { computeOverallCompleteness } from '@/lib/organization-completeness'
import { queryClient } from '@/lib/query-client'
import { meetsRoleRequirement } from '@/lib/role-access'
import { useTenant } from '@/lib/tenant'
import { cn } from '@/lib/utils'
import { signOut } from '@/requests'

const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed'

interface AppSidebarProps {
  mobileOpen: boolean
  onMobileClose: () => void
}

export function AppSidebar({ mobileOpen, onMobileClose }: AppSidebarProps) {
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const location = useLocation()
  const { resolvedTheme } = useTheme()
  const { tenantSlug, setTenant, clearTenant } = useTenant()
  const { data: organizationResult } = useOrganization()
  const organization = organizationResult?.data ?? null
  const { data: session } = authClient.useSession()
  const { data: membershipsResult } = useTenantMemberships()
  const tenants = (membershipsResult?.data ?? []).filter(
    (tenant) => tenant.status === 'ACTIVE',
  )

  const orgName = organization?.name ?? 'myTrellis'
  const logoUrl = resolveSmallLogoUrl(organization)
  const orgAvatar = logoUrl ? (
    <img
      src={logoUrl}
      alt={orgName}
      className="size-5 shrink-0 rounded border object-contain"
    />
  ) : (
    <Avatar name={orgName} size="xs" shape="square" />
  )
  const currentUser = session?.user ?? null
  const userAvatarUrl = (currentUser as { image?: string | null } | null)?.image ?? null
  const userName = currentUser?.name ?? currentUser?.email ?? ''
  const userEmail = currentUser?.email ?? ''

  const currentTenantRole = tenants.find((tenant) => tenant.slug === tenantSlug)?.role
  const completeness = computeOverallCompleteness(organization)
  const remainingSetupCount = [
    completeness.business,
    completeness.tax,
    completeness.branding,
    completeness.payment,
  ].filter((done) => !done).length
  const showCompleteSetup =
    currentTenantRole !== undefined &&
    currentTenantRole !== 'MEMBER' &&
    !completeness.isComplete

  async function handleSignOut() {
    clearTenant()
    await signOut()
    queryClient.clear()
    sessionStorage.removeItem(TWO_FACTOR_BANNER_DISMISSED_KEY)
    void navigate({ to: '/login' })
  }

  function handleSwitchTenant(slug: string, displayName: string) {
    setTenant(slug, displayName, session?.user.id ?? '')
    queryClient.clear()
    void navigate({ to: '/' })
  }

  const primaryNav: NavSection[] = [
    ...(showCompleteSetup
      ? [
          {
            items: [
              {
                to: '/onboarding',
                label: 'Complete setup',
                icon: <ClipboardCheck />,
                badge: (
                  <span className="bg-primary text-primary-foreground flex size-4.5 items-center justify-center rounded-full text-[10px] font-medium">
                    {remainingSetupCount}
                  </span>
                ),
              },
            ],
          },
        ]
      : []),
    {
      label: 'Workspace',
      items: [
        { to: '/', label: 'Overview', icon: <LayoutDashboard />, end: true },
        { to: '/clients', label: 'Clients', icon: <Users /> },
        { to: '/line-items', label: 'Line items', icon: <Package /> },
        { to: '/quotes', label: 'Quotes', icon: <ScrollText /> },
        { to: '/invoices', label: 'Invoices', icon: <FileText /> },
        { to: '/expenses', label: 'Expenses', icon: <Receipt /> },
        { to: '/reports', label: 'Reports', icon: <BarChart2 /> },
      ],
    },
  ]

  const secondaryNav: NavSection[] = [
    {
      label: 'Account',
      items: [{ to: '/settings', label: 'Settings', icon: <Settings />, end: true }],
    },
  ]

  return (
    <RootsAppSidebar
      mobileOpen={mobileOpen}
      onMobileClose={onMobileClose}
      currentPath={location.pathname}
      storageKey={SIDEBAR_COLLAPSED_KEY}
      linkComponent={Link}
      primaryNav={primaryNav}
      secondaryNav={secondaryNav}
      branding={({ collapsed }) => (
        <Link to="/" className="flex items-center gap-2.5">
          <img
            src={resolvedTheme === 'dark' ? logoMarkLight : logoMarkDark}
            alt="myTrellis"
            className="size-6 shrink-0"
          />
          {!collapsed && (
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontWeight: 900,
                letterSpacing: '-0.03em',
              }}
              className="text-base"
            >
              myTrellis
            </span>
          )}
        </Link>
      )}
      workspaceSwitcher={({ collapsed }) => (
        <DropdownMenu>
          <Tooltip touch="none">
            <TooltipTrigger asChild>
              <DropdownMenuTrigger
                aria-label={
                  collapsed ? `Switch workspace (${orgName})` : 'Switch workspace'
                }
                className={cn(
                  'hover:bg-muted flex w-full items-center rounded-lg border bg-background px-3 py-2 text-sm transition-colors focus-visible:outline-none',
                  collapsed && 'justify-center px-0 py-2',
                )}
              >
                {collapsed ? (
                  orgAvatar
                ) : (
                  <AvatarLabel
                    name={orgName}
                    subtitle={tenantSlug}
                    size="xs"
                    shape="square"
                    avatar={orgAvatar}
                    trailing={
                      <ChevronsUpDown className="text-muted-foreground size-3.5" />
                    }
                    className="flex-1 text-left"
                  />
                )}
              </DropdownMenuTrigger>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">{orgName}</TooltipContent>}
          </Tooltip>

          <DropdownMenuContent side="bottom" align="start" className="w-56">
            <DropdownMenuLabel className="text-muted-foreground text-xs font-normal tracking-wide uppercase">
              Workspaces
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {tenants.map((tenant) => (
              <DropdownMenuItem
                key={tenant.tenantId}
                onSelect={() => handleSwitchTenant(tenant.slug, tenant.displayName)}
              >
                <AvatarLabel
                  name={tenant.displayName}
                  subtitle={tenant.slug}
                  size="xs"
                  shape="square"
                  className="flex-1"
                  trailing={
                    tenant.slug === tenantSlug && (
                      <>
                        <span className="sr-only">(current)</span>
                        <Check aria-hidden className="text-primary size-3.5" />
                      </>
                    )
                  }
                />
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link
                to={
                  meetsRoleRequirement(currentTenantRole ?? null, 'ADMIN')
                    ? '/settings/organization'
                    : '/settings'
                }
                className="flex items-center gap-2"
                onClick={isMobile ? onMobileClose : undefined}
              >
                <Settings className="size-4" />
                Workspace settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <a
                href={DOCS_URL}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2"
              >
                <BookOpen className="size-4" />
                Help &amp; docs
              </a>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="flex items-center gap-2">
                <LifeBuoy className="size-4" />
                Contact support
              </a>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      userMenu={({ collapsed }) =>
        collapsed ? (
          <div className="flex flex-col items-center gap-1.5">
            <Tooltip touch="none">
              <TooltipTrigger asChild>
                <Link
                  to="/settings/profile"
                  aria-label={userName}
                  className="flex size-10 items-center justify-center"
                >
                  {userAvatarUrl ? (
                    <img
                      src={userAvatarUrl}
                      alt={userName}
                      className="size-7 shrink-0 rounded-full border object-cover"
                    />
                  ) : (
                    <Avatar name={userName} size="sm" />
                  )}
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">{userName}</TooltipContent>
            </Tooltip>
            <Tooltip touch="none">
              <TooltipTrigger asChild>
                <button
                  onClick={handleSignOut}
                  aria-label="Sign out"
                  className="text-muted-foreground hover:text-foreground hover:bg-muted flex size-7 shrink-0 items-center justify-center rounded-full transition-colors"
                >
                  <LogOut className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">Sign out</TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <div className="bg-muted/40 flex items-center gap-2.5 rounded-lg border px-3 py-2">
            {userAvatarUrl ? (
              <img
                src={userAvatarUrl}
                alt={userName}
                className="size-7 shrink-0 rounded-full border object-cover"
              />
            ) : (
              <Avatar name={userName} size="sm" />
            )}
            <Link to="/settings/profile" className="min-w-0 flex-1">
              <div className="truncate text-sm leading-tight font-medium">{userName}</div>
              <div className="text-muted-foreground truncate text-xs">{userEmail}</div>
            </Link>
            <button
              onClick={handleSignOut}
              aria-label="Sign out"
              className="text-muted-foreground hover:text-foreground hover:bg-muted flex size-7 shrink-0 items-center justify-center rounded-full transition-colors"
            >
              <LogOut className="size-3.5" />
            </button>
          </div>
        )
      }
      footer={({ collapsed }) => (
        <div
          className={cn('flex items-center gap-2', collapsed ? 'justify-center' : 'px-1')}
        >
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 font-sans text-[10px]">
                  Alpha
                </span>
              </TooltipTrigger>
              <TooltipContent side="right">{env.VITE_APP_VERSION}</TooltipContent>
            </Tooltip>
          ) : (
            <>
              <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 font-sans text-[10px]">
                Alpha
              </span>
              <Typography variant="caption" color="muted">
                {env.VITE_APP_VERSION}
              </Typography>
            </>
          )}
        </div>
      )}
    />
  )
}
